import { FormattedText, ReactPortalComp, SpanTag } from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { LandingButtonTertiary } from "@tm/ui-widgets/components";
import { theme } from "@tm/utility/global";
import React from "react";
import styled, { keyframes } from "styled-components";

import { ImageComponent } from "../../../../components/ImageComponent/ImageComponent";
import { ModalWrapper } from "../../../../components/ModalWrapper/ModalWrapper";
import type { ApiOrderDetailsRefundTrackerData } from "../../types/api";

type BottomSheetData = NonNullable<
  ApiOrderDetailsRefundTrackerData["refundCalculationBottomSheet"]
>;

interface RefundCalculationModalProps extends BottomSheetData {
  isOpen: boolean;
  onClose: () => void;
}

// ── Bottom sheet styled components ───────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const SheetBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${theme.lightBlack};
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;
`;

const SheetPanel = styled.div`
  position: relative;
  background: ${theme.whiteText};
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideUp} 0.25s ease-out forwards;
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 16px 0;
  flex-shrink: 0;
`;

const SheetCloseButton = styled.button`
  appearance: none;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const SheetBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 16px calc(20px + env(safe-area-inset-bottom));
  overflow-y: auto;
`;

// ── Modal body styled components ─────────────────────────────────────────────

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const ModalTitle = styled.div`
  margin-bottom: 16px;
`;

const LineItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid ${theme.lightSilver};

  &:last-child {
    border-bottom: none;
  }
`;

const LineItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RefundSummaryBox = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
`;

const RefundSummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const ButtonWrapper = styled.div`
  margin-top: 24px;
`;

// ── Inner content (shared between modal and sheet) ───────────────────────────

const ModalContent = ({
  title,
  lineItems,
  refundSummary,
  closeLabel,
  onClose,
  hideTitle = false,
}: BottomSheetData & { onClose: () => void; hideTitle?: boolean }) => (
  <>
    {!hideTitle && title && (
      <ModalTitle>
        <SpanTag fontWeight="black" fontSize="large">
          <FormattedText value={title} />
        </SpanTag>
      </ModalTitle>
    )}

    {lineItems && lineItems.length > 0 && (
      <LineItemsContainer>
        {lineItems.map((item, index) => (
          <LineItemRow key={index}>
            <FormattedText
              value={item.label}
              baseStyle={{
                fontWeight: "700",
                fontSize: "14px",
                lineHeight: "18px",
                color: theme.blackText,
                flex: 1,
                marginRight: "12px",
              }}
            />
            <FormattedText
              value={item.value}
              baseStyle={{
                fontWeight: "700",
                fontSize: "14px",
                lineHeight: "18px",
                color: theme.blackText,
                textAlign: "right",
                flexShrink: 0,
              }}
            />
          </LineItemRow>
        ))}
      </LineItemsContainer>
    )}

    {refundSummary && (
      <RefundSummaryBox>
        <RefundSummaryRow>
          {refundSummary.title && (
            <FormattedText
              value={refundSummary.title}
              baseStyle={{
                fontWeight: "900",
                fontSize: "16px",
                lineHeight: "20px",
                color: theme.blackText,
                flex: 1,
                marginRight: "12px",
              }}
            />
          )}
          {refundSummary.value && (
            <FormattedText
              value={refundSummary.value}
              baseStyle={{
                fontWeight: "900",
                fontSize: "18px",
                lineHeight: "22px",
                color: theme.blackText,
                textAlign: "right",
                flexShrink: 0,
              }}
            />
          )}
        </RefundSummaryRow>
        {refundSummary.subTitle && (
          <FormattedText
            value={refundSummary.subTitle}
            baseStyle={{
              fontSize: "14px",
              lineHeight: "18px",
              color: theme.greyText,
              marginTop: "8px",
            }}
          />
        )}
      </RefundSummaryBox>
    )}

    {closeLabel && (
      <ButtonWrapper>
        <LandingButtonTertiary
          title={closeLabel}
          customWidth="100%"
          customHeight="48px"
          onClick={onClose}
        />
      </ButtonWrapper>
    )}
  </>
);

// ── Public component ──────────────────────────────────────────────────────────

export const RefundCalculationModal: React.FC<RefundCalculationModalProps> = ({
  isOpen,
  onClose,
  title,
  lineItems,
  refundSummary,
  closeLabel,
}) => {
  const isMobile = useBreakpointV2() === "sm";

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <ReactPortalComp>
        <SheetBackdrop onClick={onClose}>
          <SheetPanel onClick={(e) => e.stopPropagation()}>
            <SheetHeader>
              {title && (
                <SpanTag fontWeight="black" fontSize="large">
                  <FormattedText value={title} />
                </SpanTag>
              )}
              <SheetCloseButton onClick={onClose} aria-label="Close">
                <ImageComponent
                  src="https://tripmoneycmsimgak.mmtcdn.com/img/cross_grey_rounded_e24cbc15d7.svg"
                  width="24px"
                  height="24px"
                  alt="Close"
                />
              </SheetCloseButton>
            </SheetHeader>
            <SheetBody>
              <ModalContent
                title={title}
                lineItems={lineItems}
                refundSummary={refundSummary}
                closeLabel={closeLabel}
                onClose={onClose}
                hideTitle
              />
            </SheetBody>
          </SheetPanel>
        </SheetBackdrop>
      </ReactPortalComp>
    );
  }

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} customWidth="480px">
      <ModalBody>
        <ModalContent
          title={title}
          lineItems={lineItems}
          refundSummary={refundSummary}
          closeLabel={closeLabel}
          onClose={onClose}
        />
      </ModalBody>
    </ModalWrapper>
  );
};

export default RefundCalculationModal;
